export function workdayMapping() {
  return {
    // Workday-specific data properties
    teamNamesStart: "",
    teamNamesEnd: "",
    teamDRIsStart: "",
    teamDRIsEnd: "",
    teamIDsStart: "",
    teamIDsEnd: "",
    teamNamesOffset: 0,
    teamDRIsOffset: 0,
    teamIDsOffset: 0,

    init() {
      this.autoDetectWorkdayColumns();
    },

    // Auto-detect Workday columns based on naming patterns
    autoDetectWorkdayColumns() {
      // Find supervisory org columns and their levels
      const supervisoryOrgCols = this.headers.filter((header) =>
        header.toLowerCase().includes("supervisory_org"),
      );
      const managerCols = this.headers.filter(
        (header) =>
          header.toLowerCase().includes("manager") &&
          header.toLowerCase().includes("level") &&
          !header.toLowerCase().includes("name"), // Exclude name columns
      );

      if (supervisoryOrgCols.length) {
        // Extract level numbers using regex for names and IDs separately
        const namePattern = /supervisory_org_name.*level_(\d+)/i;
        const idPattern = /supervisory_org_id.*level_(\d+)/i;
        const managerPattern = /manager.*level[_-]?(\d+)/i; // More flexible pattern
        const namesMap = new Map();
        const idsMap = new Map();
        const managersMap = new Map();

        supervisoryOrgCols.forEach((col) => {
          const nameMatch = col.toLowerCase().match(namePattern);
          const idMatch = col.toLowerCase().match(idPattern);

          if (nameMatch) {
            const levelNum = parseInt(nameMatch[1], 10);
            namesMap.set(levelNum, col);
          } else if (idMatch) {
            const levelNum = parseInt(idMatch[1], 10);
            idsMap.set(levelNum, col);
          }
        });

        managerCols.forEach((col) => {
          const match = col.toLowerCase().match(managerPattern);
          if (match) {
            const levelNum = parseInt(match[1], 10);
            managersMap.set(levelNum, col);
          }
        });

        if (namesMap.size > 0) {
          const nameLevels = Array.from(namesMap.keys()).sort((a, b) => a - b);
          const minNameLevel = nameLevels[0];
          const maxNameLevel = nameLevels[nameLevels.length - 1];
          this.teamNamesStart = namesMap.get(minNameLevel);
          this.teamNamesEnd = namesMap.get(maxNameLevel);
        }

        if (idsMap.size > 0) {
          const idLevels = Array.from(idsMap.keys()).sort((a, b) => a - b);
          const minIdLevel = idLevels[0];
          const maxIdLevel = idLevels[idLevels.length - 1];
          this.teamIDsStart = idsMap.get(minIdLevel);
          this.teamIDsEnd = idsMap.get(maxIdLevel);
        }

        if (managersMap.size > 0) {
          const managerLevels = Array.from(managersMap.keys()).sort(
            (a, b) => a - b,
          );
          const minManagerLevel = managerLevels[0];
          const maxManagerLevel = managerLevels[managerLevels.length - 1];
          this.teamDRIsStart = managersMap.get(minManagerLevel);
          this.teamDRIsEnd = managersMap.get(maxManagerLevel);
        }

        // Trigger the preview update if we found any matches
        if (namesMap.size > 0 || idsMap.size > 0 || managersMap.size > 0) {
          this.updateManipulatedPreview();
        }
      }
    },

    // Get column range between start and end columns
    getColumnRange(startCol, endCol) {
      const startIndex = this.headers.indexOf(startCol);
      const endIndex = this.headers.indexOf(endCol);

      if (startIndex === -1 || endIndex === -1) return [];

      const start = Math.min(startIndex, endIndex);
      const end = Math.max(startIndex, endIndex);

      return this.headers.slice(start, end + 1);
    },

    // Reverse team names range
    reverseTeamNamesRange() {
      [this.teamNamesStart, this.teamNamesEnd] = [
        this.teamNamesEnd,
        this.teamNamesStart,
      ];
      this.updateManipulatedPreview();
    },

    // Reverse team DRIs range
    reverseTeamDRIsRange() {
      [this.teamDRIsStart, this.teamDRIsEnd] = [
        this.teamDRIsEnd,
        this.teamDRIsStart,
      ];
      this.updateManipulatedPreview();
    },

    // Reverse team IDs range
    reverseTeamIDsRange() {
      [this.teamIDsStart, this.teamIDsEnd] = [
        this.teamIDsEnd,
        this.teamIDsStart,
      ];
      this.updateManipulatedPreview();
    },

    // Shift team names up in hierarchy
    shiftTeamNamesUp() {
      this.teamNamesOffset = (this.teamNamesOffset || 0) + 1;
      this.updateManipulatedPreview();
    },

    // Shift team names down in hierarchy
    shiftTeamNamesDown() {
      this.teamNamesOffset = (this.teamNamesOffset || 0) - 1;
      if (this.teamNamesOffset < 0) this.teamNamesOffset = 0;
      this.updateManipulatedPreview();
    },

    // Shift team DRIs up in hierarchy
    shiftTeamDRIsUp() {
      this.teamDRIsOffset = (this.teamDRIsOffset || 0) + 1;
      this.updateManipulatedPreview();
    },

    // Shift team DRIs down in hierarchy
    shiftTeamDRIsDown() {
      this.teamDRIsOffset = (this.teamDRIsOffset || 0) - 1;
      if (this.teamDRIsOffset < 0) this.teamDRIsOffset = 0;
      this.updateManipulatedPreview();
    },

    // Shift team IDs up in hierarchy
    shiftTeamIDsUp() {
      this.teamIDsOffset = (this.teamIDsOffset || 0) + 1;
      this.updateManipulatedPreview();
    },

    // Shift team IDs down in hierarchy
    shiftTeamIDsDown() {
      this.teamIDsOffset = (this.teamIDsOffset || 0) - 1;
      if (this.teamIDsOffset < 0) this.teamIDsOffset = 0;
      this.updateManipulatedPreview();
    },

    // Update the manipulated preview based on current settings
    updateManipulatedPreview() {
      const teamNameCols = this.getColumnRange(
        this.teamNamesStart,
        this.teamNamesEnd,
      );
      const teamDRICols = this.getColumnRange(
        this.teamDRIsStart,
        this.teamDRIsEnd,
      );
      const teamIDCols = this.getColumnRange(
        this.teamIDsStart,
        this.teamIDsEnd,
      );

      this.manipulatedRows = this.rows.map((row) => {
        // Start with all original fields
        const newRow = { ...row };

        // Add our standard mapped fields with empty defaults
        const mappedFields = {
          team_name: "",
          mgr_email: "",
          parent_team: "",
          parent_team_dri: "",
          grandparent_team: "",
          grandparent_team_dri: "",
          team_reference_id: "",
          parent_team_reference_id: "",
          grandparent_team_reference_id: "",
        };

        // Merge the original data with our mapped fields
        Object.assign(newRow, mappedFields);

        // Process team names
        let nonEmptyTeams = teamNameCols
          .map((col) => row[col]?.trim())
          .filter((name) => name !== "");

        if (
          this.headers.indexOf(this.teamNamesEnd) <
          this.headers.indexOf(this.teamNamesStart)
        ) {
          nonEmptyTeams = nonEmptyTeams.reverse();
        }

        // Apply offset to the mapping
        const teamOffset = this.teamNamesOffset || 0;
        nonEmptyTeams.forEach((teamName, index) => {
          const adjustedIndex = index + teamOffset;
          const teamKey =
            adjustedIndex === 0
              ? "team_name"
              : adjustedIndex === 1
                ? "parent_team"
                : adjustedIndex === 2
                  ? "grandparent_team"
                  : `extra_level_${adjustedIndex - 2}`;
          newRow[teamKey] = teamName;
        });

        // Process team DRIs
        let nonEmptyDRIs = teamDRICols
          .map((col) => row[col]?.trim())
          .filter((dri) => dri !== "");

        if (
          this.headers.indexOf(this.teamDRIsEnd) <
          this.headers.indexOf(this.teamDRIsStart)
        ) {
          nonEmptyDRIs = nonEmptyDRIs.reverse();
        }

        const driOffset = this.teamDRIsOffset || 0;
        nonEmptyDRIs.forEach((dri, index) => {
          const adjustedIndex = index + driOffset;
          const driKey =
            adjustedIndex === 0
              ? "mgr_email"
              : adjustedIndex === 1
                ? "parent_team_dri"
                : adjustedIndex === 2
                  ? "grandparent_team_dri"
                  : `extra_level_${adjustedIndex - 2}_dri`;
          newRow[driKey] = dri;
        });

        // Process team IDs
        let nonEmptyIDs = teamIDCols
          .map((col) => row[col]?.trim())
          .filter((id) => id !== "");

        if (
          this.headers.indexOf(this.teamIDsEnd) <
          this.headers.indexOf(this.teamIDsStart)
        ) {
          nonEmptyIDs = nonEmptyIDs.reverse();
        }

        const idOffset = this.teamIDsOffset || 0;
        nonEmptyIDs.forEach((id, index) => {
          const adjustedIndex = index + idOffset;
          const idKey =
            adjustedIndex === 0
              ? "team_reference_id"
              : adjustedIndex === 1
                ? "parent_team_reference_id"
                : adjustedIndex === 2
                  ? "grandparent_team_reference_id"
                  : `extra_level_${adjustedIndex - 2}_reference_id`;
          newRow[idKey] = id;
        });

        return newRow;
      });

      this.sampledRows = this.manipulatedRows.slice(0, 20);
    },

    // Compress hierarchy levels by removing gaps
    compressLevels(employee) {
      // Define the levels in order from lowest to highest
      const levels = [
        {
          name: "team",
          dri: "mgr_email",
          ref: "team_reference_id",
        },
        {
          name: "parent_team",
          dri: "parent_team_dri",
          ref: "parent_team_reference_id",
        },
        {
          name: "grandparent_team",
          dri: "grandparent_team_dri",
          ref: "grandparent_team_reference_id",
        },
        ...Array.from({ length: 19 }, (_, i) => ({
          name: `extra_level_${i + 1}`,
          dri: `extra_level_${i + 1}_dri`,
          ref: `extra_level_${i + 1}_reference_id`,
        })),
      ];

      // Create a copy of the employee object
      const result = { ...employee };

      // Find first gap (where a level is empty but higher levels have data)
      for (let i = 0; i < levels.length - 1; i++) {
        const currentLevel = levels[i];

        // If current level is empty
        if (!result[currentLevel.name]) {
          // Look for the next non-empty level
          for (let j = i + 1; j < levels.length; j++) {
            const higherLevel = levels[j];

            // If we find a populated higher level
            if (result[higherLevel.name]) {
              // Move this level down to the gap
              result[currentLevel.name] = result[higherLevel.name];
              result[currentLevel.dri] = result[higherLevel.dri];
              result[currentLevel.ref] = result[higherLevel.ref];

              // Clear the higher level
              result[higherLevel.name] = null;
              result[higherLevel.dri] = null;
              result[higherLevel.ref] = null;

              // Break since we've filled this gap
              break;
            }
          }
        }
      }

      return result;
    },

    // Get the manipulated data ready for processing
    getManipulatedData() {
      return {
        rows: this.manipulatedRows,
        headers: Object.keys(this.manipulatedRows[0] || {}),
        extraLevels: this.getExtraLevels(),
      };
    },

    // Get extra levels that actually have data
    getExtraLevels() {
      if (!this.manipulatedRows.length) return [];

      const extraLevelKeys = Object.keys(this.manipulatedRows[0] || {})
        .filter(
          (key) =>
            key.startsWith("extra_level_") &&
            !key.includes("dri") &&
            !key.includes("reference"),
        )
        .map((key) => parseInt(key.split("_")[2]));

      return extraLevelKeys.sort((a, b) => a - b);
    },

    // Reset all workday mapping settings
    reset() {
      this.teamNamesStart = "";
      this.teamNamesEnd = "";
      this.teamDRIsStart = "";
      this.teamDRIsEnd = "";
      this.teamIDsStart = "";
      this.teamIDsEnd = "";
      this.manipulatedRows = [];
      this.teamNamesOffset = 0;
      this.teamDRIsOffset = 0;
      this.teamIDsOffset = 0;
      this.headers = [];
      this.rows = [];
      this.sampledRows = [];
      this.extraLevels = [];
    },
  };
}
